import { Box, Typography } from '@mui/material'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { BlueButton } from '../../../customComponents/buttons/BlueButton'
import { OrganizationCard, type OrganizationCardChanges } from './OrganizationCard'
import { styles } from './OrganizationCard.styles'
import type { MembershipCapabilities } from './cardPermissions'
import type { SecurityLevel, SelectableOption, UserPermission } from '../../../../utils/genericTypes'
import type { UserOrganization } from '../../../../models/UserOrganization'

interface Props {
  organizations: UserOrganization[]
  newOrganizationIds?: number[]
  canSetDefault: boolean
  capabilitiesFor: (org: UserOrganization, isNewMembership: boolean) => MembershipCapabilities
  editsInline?: boolean
  onSetDefault: (orgId: number) => Promise<void>
  onSave?: (org: UserOrganization, changes: OrganizationCardChanges) => Promise<void>
  onCancel?: (org: UserOrganization) => void
  onRemove?: (org: UserOrganization) => void
  onDirtyChange?: (orgId: number, isDirty: boolean) => void
  onAddOrganization?: () => void
  loadBuildingOptions?: (orgId: number) => Promise<SelectableOption[]>
  loadUserFunctions?: (orgId: number) => Promise<string[] | null>
  loadPermissionOptions?: (securityLevel: SecurityLevel) => Promise<UserPermission[]>
}

export const OrganizationCardList = (props: Props): JSX.Element =>
  <Box sx={styles.panel} data-testid='organizationsPanel'>
    <Box sx={styles.panelHeader}>
      <Typography variant='h4' sx={styles.panelTitle}>Organizations</Typography>

      {props.onAddOrganization != null && <BlueButton
        onClick={props.onAddOrganization}
        endIcon={<AddCircleIcon sx={{ fontSize: '16px' }} />}
        dataTestId='addOrg'
      >
        Add New
      </BlueButton>}
    </Box>

    {props.organizations.map((org) => {
      const isNew = props.newOrganizationIds?.includes(org.id) === true
      const capabilities = props.capabilitiesFor(org, isNew)

      return <OrganizationCard
        key={org.id}
        org={org}
        isNew={isNew}
        canSetDefault={props.canSetDefault}
        canEditBuildings={capabilities.canEditBuildings}
        canEditRole={capabilities.canEditRole}
        canEditFunctions={capabilities.canEditFunctions}
        canRemove={capabilities.canRemove}
        editsInline={props.editsInline}
        onSetDefault={props.onSetDefault}
        onSave={props.onSave}
        onCancel={props.onCancel}
        onRemove={props.onRemove}
        onDirtyChange={props.onDirtyChange}
        loadBuildingOptions={props.loadBuildingOptions}
        loadUserFunctions={props.loadUserFunctions}
        loadPermissionOptions={props.loadPermissionOptions}
      />
    })}

    {props.organizations.length === 0 &&
      <Typography variant='body1' data-testid='noOrganizations'>No organizations</Typography>}
  </Box>
